import React from 'react';

import { IconText } from '~/src/dash/components/IconText';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { FONT_WEIGHT, fontSizing } from '~/src/dash/components/text/common';
import { borderRadius, ButtonV2, spacing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  IPlatformConnectionDealExtRow,
  IPlatformConnectionUniqueDemandRow,
  orderDirection,
} from '~/src/dash/views/platform-connect/common';

interface IDraftOfferNewFilters {
  key: string;
  label: string;
  name: string;
  tooltipText?: string;
}

interface IDraftOfferNewTable {
  columns: IListViewTableColumns;
}

export interface IOfferNewConfig {
  dealExtension: IOfferTypeConfig;
  uniqueDemand: IOfferTypeConfig;
}

interface IOfferTypeConfig {
  cypressClassName: string;
  activeColumnKeys: string[];
  orderDirection: orderDirection;
  orderBy: string;
  page: number;
  rowsPerPage: RowsPerPage;
  filters: IDraftOfferNewFilters[];
  table: IDraftOfferNewTable;
}

export const formatDealExtensionDataToLVTRows = (
  data: IPlatformConnectionDealExtRow[],
  cfg: IOfferNewConfig,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  data.forEach((datum) => {
    const row = {} as IListViewTableRow;
    const columns = Object.values(cfg.dealExtension.table.columns) || [];
    columns.forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IPlatformConnectionDealExtRow];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

export const formatUniqueDemandDataToLVTRows = (
  data: IPlatformConnectionUniqueDemandRow[],
  cfg: IOfferNewConfig,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  data.forEach((datum) => {
    const row = {} as IListViewTableRow;
    const columns = Object.values(cfg.uniqueDemand.table.columns) || [];
    columns.forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IPlatformConnectionUniqueDemandRow];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

const DealExtDrawerModalRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {props.row.Status.value.HasPendingOffer && (
        <IconText
          small
          bold
          text='Offer Sent'
          icon='success-check-mark'
          iconColor={publicaColors.green}
        />
      )}
      {props.row.Status.value.HasConnection && (
        <IconText
          small
          bold
          text='Connection Made'
          icon='success-check-mark'
          iconColor={publicaColors.green}
        />
      )}
      {!props.row.Status.value.HasPendingOffer && !props.row.Status.value.HasConnection && (
        <ButtonV2
          clearBackground
          hoverColor={publicaColors.lighterGray}
          style={{
            display: 'flex',
            border: `1px solid ${publicaColors.blue}`,
            borderRadius: borderRadius.small,
            padding: '0px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => customFunctions.handleClickOpenDrawer(props)}
        >
          {' '}
          <IconText small bold color={publicaColors.blue} text='Make an Offer' icon='paper-plane' />
        </ButtonV2>
      )}
    </div>
  );
};

const UniqueDemandDrawerModalRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <IconText
        small
        bold
        style={{
          border: `1px solid ${publicaColors.blue}`,
        }}
        color={publicaColors.blue}
        text='Make an Offer'
        icon='paper-plane'
        onClick={() => customFunctions.handleClickOpenDrawer(props)}
      />
    </div>
  );
};

export const draftOfferNewConfig = {
  dealExtension: {
    cypressClassName: 'PlatformConnectDraftOfferNew',
    activeColumnKeys: ['Name', 'DealExtensionActions'],
    orderDirection: 'desc',
    orderBy: 'Partner',
    page: 0,
    rowsPerPage: 25,
    filters: [
      {
        key: 'deal',
        label: 'Deal',
        name: 'Deal',
        tooltipText: 'Only your existing deals in Publica can be selected from the dropdown.',
      },
      {
        key: 'impressionsGoal',
        label: 'Impressions Goal',
        name: 'ImpressionsGoal',
        tooltipText:
          "This Impression Goal entry indicates your (the buyer's) goal that the seller can see when opting in. However, this is not a forecast of available impressions for the seller.",
      },
    ],
    table: {
      columns: {
        ID: {
          formatValue: (value: number) => String(value),
          isHidden: true,
          isSearchable: true,
          key: 'ID',
          label: 'ID',
          requestKey: 'ID',
          responseKey: 'ID',
          valueType: 'string',
        },
        Status: {
          isHidden: true,
          key: 'Status',
          label: 'Status',
          requestKey: 'Status',
          responseKey: 'Status',
          valueType: 'object',
        },
        Name: {
          cellRenderType: 'name_link',
          cellStyle: {
            fontSize: fontSizing.small,
            fontWeight: FONT_WEIGHT.base,
            verticalAlign: 'middle',
            textAlign: 'center',
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: spacing.medium,
          },
          disableSort: true,
          defaultOrderDirection: 'asc',
          headerRenderType: 'centered_text',
          headerStyle: {
            fontSize: fontSizing.small,
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: '0px 16px 8px 16px',
          },
          isSearchable: true,
          key: 'Name',
          label: 'Seller',
          requestKey: '',
          responseKey: 'Name',
          valueType: 'string',
        },
        DealExtensionActions: {
          cellJSX: DealExtDrawerModalRender,
          cellStyle: {
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: spacing.medium,
            width: '100px',
          },
          disableSort: true,
          headerRenderType: 'centered_text',
          headerStyle: {
            fontSize: fontSizing.small,
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: '0px 16px 8px 16px',
          },
          key: 'DealExtensionActions',
          label: 'Action',
          requestKey: '',
          responseKey: '',
          valueType: 'component',
        },
      },
    },
  },
  uniqueDemand: {
    cypressClassName: 'PlatformConnectDraftOfferNew',
    activeColumnKeys: ['CustomLeftColumn', 'Name', 'UniqueDemandActions'],
    orderDirection: 'desc',
    orderBy: 'Partner',
    page: 0,
    rowsPerPage: 25,
    filters: [
      {
        key: 'demandSource',
        label: 'Demand Source',
        name: 'DemandSource',
        tooltipText: `After selecting a demand source, you will see sellers' opportunities that would benefit from your unique demand over their inventory. Only demand sources with active bidders are displayed in the dropdown.`,
      },
    ],
    table: {
      columns: {
        ID: {
          formatValue: (value: number) => String(value),
          isHidden: true,
          isSearchable: true,
          key: 'ID',
          label: 'ID',
          requestKey: 'ID',
          responseKey: 'ID',
          valueType: 'string',
        },
        CustomLeftColumn: {
          cellRenderType: 'name_link',
          cellStyle: {
            fontSize: fontSizing.small,
            verticalAlign: 'middle',
            textAlign: 'center',
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: spacing.medium,
            wordBreak: 'break-word',
          },
          disableSort: true,
          defaultOrderDirection: 'asc',
          headerRenderType: 'centered_text',
          headerStyle: {
            fontSize: fontSizing.small,
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: '0px 16px 8px 16px',
            verticalAlign: 'middle',
          },
          isSearchable: true,
          key: 'CustomLeftColumn',
          label: 'Demand Match',
          requestKey: '',
          responseKey: 'DemandMatch',
          valueType: 'string',
        },
        Name: {
          cellRenderType: 'name_link',
          cellStyle: {
            fontSize: fontSizing.small,
            fontWeight: FONT_WEIGHT.base,
            verticalAlign: 'middle',
            textAlign: 'center',
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: spacing.medium,
            wordBreak: 'break-word',
          },
          disableSort: true,
          defaultOrderDirection: 'asc',
          headerRenderType: 'centered_text',
          headerStyle: {
            fontSize: fontSizing.small,
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: '0px 16px 8px 16px',
          },
          isSearchable: true,
          key: 'Name',
          label: 'Seller',
          requestKey: '',
          responseKey: 'Name',
          valueType: 'string',
        },
        UniqueDemandActions: {
          cellJSX: UniqueDemandDrawerModalRender,
          cellStyle: {
            verticalAlign: 'middle',
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: spacing.medium,
            width: '100px',
          },
          disableSort: true,
          headerRenderType: 'centered_text',
          headerStyle: {
            fontSize: fontSizing.small,
            borderBottom: `1px solid ${publicaColors.lighterGray}`,
            padding: '0px 16px 8px 16px',
          },
          key: 'UniqueDemandActions',
          label: 'Action',
          requestKey: '',
          responseKey: '',
          valueType: 'component',
        },
      },
    },
  },
} as IOfferNewConfig;
